import React, { useEffect, useState, useCallback, useRef } from 'react';
import { GoogleMap, Polygon, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Drawer } from './ui/Drawer';
import Toggle from './Toggle';
import api from '../services/api';
import Notification, { NotificationContainer } from './Notification';
import { getGoogleMapsLoaderOptions } from '../config/googleMaps';

interface GeofenceData {
  id: string | number;
  location_type?: string;
  lname: string;
  ladd?: string;
  lcity?: string;
  lstate?: string;
  lzip?: string;
  rcvhours1?: string;
  rcvhours2?: string;
  droplive?: string;
  communication?: string;
  type: string;
  location: string;
  coordinates?: string;
  radius?: number;
  status: string;
  alias?: string;
  created_at?: string;
  updated_at?: string;
}

/** Geometry persisted by a save, handed back so callers (e.g. the location edit
 *  drawer) can keep their own form state in sync without a re-fetch. */
export interface SavedGeofenceGeometry {
  coordinates: string;
  geofencetype: 'CIRCULAR' | 'POLYGON';
  radius: number;
}

interface GeofenceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  geofence: GeofenceData | null;
  onSave?: (saved?: SavedGeofenceGeometry) => void;
}

type GeofenceType = 'Circle' | 'Polygon';

// Conversion constants
const METERS_PER_MILE = 1609.34;
const MILES_PER_METER = 1 / 1609.34;

// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '500px',
};

// Default center (Houston, TX)
const defaultCenter = {
  lat: 29.7604,
  lng: -95.3698,
};

// Map options
const getMapOptions = (isDarkMode: boolean): google.maps.MapOptions => ({
  mapId: '3cf64d0fcebdaf11f3625d6e',
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: true,
  streetViewControl: false,
  fullscreenControl: true,
  clickableIcons: false,
  colorScheme: isDarkMode ? 'DARK' : 'LIGHT',
  mapTypeId: 'satellite', // Show satellite view for geofence editing
});

export const GeofenceDrawer: React.FC<GeofenceDrawerProps> = ({
  isOpen,
  onClose,
  geofence,
  onSave,
}) => {
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(12);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [geofenceType, setGeofenceType] = useState<GeofenceType>('Circle');
  const [radiusMiles, setRadiusMiles] = useState(0.5); // Radius in miles
  const [polygonPoints, setPolygonPoints] = useState<google.maps.LatLngLiteral[]>([]);
  const [circleCenter, setCircleCenter] = useState<google.maps.LatLngLiteral | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; title: string; message: string } | null>(null);
  const [selectedPointIndex, setSelectedPointIndex] = useState<number | null>(null);

  // Refs for shapes - keep track of ALL circles to clean them up
  const circleRef = useRef<google.maps.Circle | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const manualCircleRef = useRef<google.maps.Circle | null>(null);

  // Load Google Maps
  const { isLoaded, loadError } = useJsApiLoader(getGoogleMapsLoaderOptions());

  // Detect dark mode
  useEffect(() => {
    const isDark = document.documentElement.classList.contains('dark');
    setIsDarkMode(isDark);

    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  // Parse coordinates and initialize edit state when geofence changes
  useEffect(() => {
    if (geofence?.coordinates) {
      try {
        // Check if it's a polygon (semicolon-separated) or circle (single point)
        if (geofence.coordinates.includes(';')) {
          // Polygon
          const points = geofence.coordinates.split(';').map(coord => {
            const [lat, lng] = coord.split(',').map(Number);
            return { lat, lng };
          });

          setGeofenceType('Polygon');
          setPolygonPoints(points);

          if (points.length > 0) {
            const avgLat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
            const avgLng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
            setMapCenter({ lat: avgLat, lng: avgLng });
            setMapZoom(13);
          }
        } else if (geofence.coordinates.includes(',')) {
          // Circle
          const [lat, lng] = geofence.coordinates.split(',').map(Number);
          if (!isNaN(lat) && !isNaN(lng)) {
            setGeofenceType('Circle');
            setCircleCenter({ lat, lng });
            setMapCenter({ lat, lng });
            setMapZoom(14);
            // Radius is in miles in the database
            setRadiusMiles(geofence.radius || 0.5); // Default to 0.5 miles
          }
        }
      } catch (error) {
        console.error('Error parsing coordinates:', error);
      }
    }
  }, [geofence]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapLoaded(true);
  }, []);

  // Handle map click when editing
  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (!isEditing || !e.latLng) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    if (geofenceType === 'Circle') {
      // Set circle center
      setCircleCenter({ lat, lng });
    } else {
      // Add point to polygon
      setPolygonPoints(prev => [...prev, { lat, lng }]);
    }
  }, [isEditing, geofenceType]);

  // Clear polygon points
  const handleClearPolygon = () => {
    setPolygonPoints([]);
  };

  // Undo last polygon point
  const handleUndoPoint = () => {
    if (polygonRef.current) {
      const path = polygonRef.current.getPath();
      if (path.getLength() > 0) {
        path.removeAt(path.getLength() - 1);
      }
    } else {
      setPolygonPoints(prev => prev.slice(0, -1));
    }
  };

  // Remove specific polygon point by index
  const handleRemovePoint = (index: number) => {
    if (polygonRef.current) {
      const path = polygonRef.current.getPath();
      path.removeAt(index);

      // Force immediate state update
      const points: google.maps.LatLngLiteral[] = [];
      for (let i = 0; i < path.getLength(); i++) {
        const point = path.getAt(i);
        points.push({ lat: point.lat(), lng: point.lng() });
      }
      setPolygonPoints(points);
    } else {
      setPolygonPoints(prev => prev.filter((_, i) => i !== index));
    }

    // Clear selection if we deleted the selected point
    if (selectedPointIndex === index) {
      setSelectedPointIndex(null);
    }
  };

  // Toggle edit mode
  const handleToggleEdit = () => {
    if (isEditing) {
      // Cancel editing - reset to original values
      if (geofence?.coordinates) {
        if (geofence.coordinates.includes(';')) {
          const points = geofence.coordinates.split(';').map(coord => {
            const [lat, lng] = coord.split(',').map(Number);
            return { lat, lng };
          });
          setPolygonPoints(points);
        } else if (geofence.coordinates.includes(',')) {
          const [lat, lng] = geofence.coordinates.split(',').map(Number);
          setCircleCenter({ lat, lng });
        }
      }
    }
    setIsEditing(!isEditing);
  };

  // Save changes
  const handleSave = async () => {
    if (!geofence) return;

    setIsSaving(true);
    try {
      let coordinatesString = '';
      let finalRadiusMiles = radiusMiles;

      if (geofenceType === 'Circle') {
        // Get final values from the circle ref (in case it was dragged)
        if (circleRef.current) {
          const center = circleRef.current.getCenter();
          const radiusMeters = circleRef.current.getRadius();

          if (center) {
            coordinatesString = `${center.lat()},${center.lng()}`;
          }
          finalRadiusMiles = radiusMeters * MILES_PER_METER;
        } else if (circleCenter) {
          // Fallback to state if ref not available
          coordinatesString = `${circleCenter.lat},${circleCenter.lng}`;
        }
      } else if (geofenceType === 'Polygon') {
        // Get final values from polygon ref (in case it was edited)
        if (polygonRef.current) {
          const path = polygonRef.current.getPath();
          const points: google.maps.LatLngLiteral[] = [];

          for (let i = 0; i < path.getLength(); i++) {
            const point = path.getAt(i);
            points.push({ lat: point.lat(), lng: point.lng() });
          }

          if (points.length < 3) {
            setNotification({
              type: 'error',
              title: 'Invalid Geofence',
              message: 'Polygon must have at least 3 points',
            });
            setIsSaving(false);
            return;
          }

          coordinatesString = points.map(p => `${p.lat},${p.lng}`).join(';');
        } else if (polygonPoints.length >= 3) {
          // Fallback to state
          coordinatesString = polygonPoints.map(p => `${p.lat},${p.lng}`).join(';');
        } else {
          setNotification({
            type: 'error',
            title: 'Invalid Geofence',
            message: 'Polygon must have at least 3 points',
          });
          setIsSaving(false);
          return;
        }
      }

      // Update coordinates
      await api.patch(`/geofences/${geofence.id}`, {
        field: 'coordinates',
        value: coordinatesString,
      });

      // Update geofencetype based on current shape
      await api.patch(`/geofences/${geofence.id}`, {
        field: 'geofencetype',
        value: geofenceType === 'Circle' ? 'CIRCULAR' : 'POLYGON',
      });

      // If Circle, also update radius (in miles)
      if (geofenceType === 'Circle') {
        await api.patch(`/geofences/${geofence.id}`, {
          field: 'radius',
          value: Number(finalRadiusMiles.toFixed(2)),
        });
      }

      setNotification({
        type: 'success',
        title: 'Saved',
        message: 'Geofence updated successfully',
      });

      setIsEditing(false);

      // Call onSave callback if provided, handing back the saved geometry so
      // callers can sync their own state without re-fetching.
      if (onSave) {
        onSave({
          coordinates: coordinatesString,
          geofencetype: geofenceType === 'Circle' ? 'CIRCULAR' : 'POLYGON',
          radius: Number(finalRadiusMiles.toFixed(2)),
        });
      }

      // Close drawer after successful save
      setTimeout(() => {
        onClose();
      }, 1000); // Wait 1 second to show the success message
    } catch (error) {
      console.error('Error saving geofence:', error);
      setNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to save geofence',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onPolygonLoad = useCallback((polygon: google.maps.Polygon) => {
    polygonRef.current = polygon;

    // Listen to path changes
    if (isEditing) {
      const path = polygon.getPath();

      const updatePoints = () => {
        const points: google.maps.LatLngLiteral[] = [];
        for (let i = 0; i < path.getLength(); i++) {
          const point = path.getAt(i);
          points.push({ lat: point.lat(), lng: point.lng() });
        }
        setPolygonPoints(points);
      };

      // Update points when they change
      google.maps.event.addListener(path, 'set_at', updatePoints);
      google.maps.event.addListener(path, 'insert_at', updatePoints);
      google.maps.event.addListener(path, 'remove_at', updatePoints);
    }
  }, [isEditing]);

  // Effect to cleanup manual circle when drawer closes
  useEffect(() => {
    if (!isOpen && manualCircleRef.current) {
      manualCircleRef.current.setMap(null);
      google.maps.event.clearInstanceListeners(manualCircleRef.current);
      manualCircleRef.current = null;
      circleRef.current = null;
    }
  }, [isOpen]);

  // Manually create and manage the circle to avoid React component issues
  useEffect(() => {
    if (!isOpen || !mapRef.current || geofenceType !== 'Circle' || !circleCenter) return;

    // Remove old manual circle
    if (manualCircleRef.current) {
      manualCircleRef.current.setMap(null);
      google.maps.event.clearInstanceListeners(manualCircleRef.current);
      manualCircleRef.current = null;
    }

    // Create new circle manually
    const radiusInMeters = radiusMiles * METERS_PER_MILE;
    const newCircle = new google.maps.Circle({
      center: circleCenter,
      radius: radiusInMeters,
      map: mapRef.current,
      fillColor: geofence?.status === 'active' ? '#3B82F6' : '#9CA3AF',
      fillOpacity: 0.3,
      strokeColor: geofence?.status === 'active' ? '#1D4ED8' : '#6B7280',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      editable: isEditing,
      draggable: isEditing,
    });

    manualCircleRef.current = newCircle;
    circleRef.current = newCircle;

    // Add listeners to update state when circle is edited
    if (isEditing) {
      let updateTimeout: NodeJS.Timeout;

      newCircle.addListener('radius_changed', () => {
        clearTimeout(updateTimeout);
        updateTimeout = setTimeout(() => {
          const newRadiusMeters = newCircle.getRadius();
          const newRadiusMiles = newRadiusMeters * MILES_PER_METER;
          setRadiusMiles(Number(newRadiusMiles.toFixed(2)));
        }, 100); // Debounce to avoid too many updates
      });

      newCircle.addListener('center_changed', () => {
        clearTimeout(updateTimeout);
        updateTimeout = setTimeout(() => {
          const center = newCircle.getCenter();
          if (center) {
            setCircleCenter({ lat: center.lat(), lng: center.lng() });
          }
        }, 100); // Debounce to avoid too many updates
      });
    }

    // Cleanup when unmounting or dependencies change
    return () => {
      if (manualCircleRef.current) {
        manualCircleRef.current.setMap(null);
        google.maps.event.clearInstanceListeners(manualCircleRef.current);
        manualCircleRef.current = null;
      }
    };
  }, [isOpen, mapLoaded, circleCenter, radiusMiles, isEditing, geofenceType, geofence?.status]);

  const renderGeofence = () => {
    if (geofenceType !== 'Circle' || !circleCenter) {
      // Render polygon or nothing
      if (geofenceType === 'Polygon' && polygonPoints.length >= 1) {
        return (
          <>
            <Polygon
              key={`polygon-${geofence?.id}`}
              paths={polygonPoints}
              onLoad={onPolygonLoad}
              options={{
                fillColor: geofence?.status === 'active' ? '#3B82F6' : '#9CA3AF',
                fillOpacity: polygonPoints.length >= 3 ? 0.3 : 0.1,
                strokeColor: geofence?.status === 'active' ? '#1D4ED8' : '#6B7280',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                editable: isEditing,
                draggable: isEditing,
              }}
            />
            {/* Show marker for selected point */}
            {selectedPointIndex !== null && polygonPoints[selectedPointIndex] && (
              <Marker
                position={polygonPoints[selectedPointIndex]}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 10,
                  fillColor: '#3B82F6',
                  fillOpacity: 0.8,
                  strokeColor: '#FFFFFF',
                  strokeWeight: 3,
                }}
              />
            )}
          </>
        );
      }
      return null;
    }

    // Circle is now created manually via useEffect above
    return null;
  };

  if (!geofence) return null;

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title={`Geofence: ${geofence.lname}`}
        width="50%"
        footer={
          isEditing ? (
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleToggleEdit}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          ) : (
            <button
              onClick={handleToggleEdit}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Edit Geofence
            </button>
          )
        }
      >
        <div className="space-y-6">
          {/* Map */}
          <div className="bg-panel rounded-lg p-4 shadow">
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-heading">Map View</h3>
                {isEditing && geofenceType === 'Polygon' && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleUndoPoint}
                      disabled={polygonPoints.length === 0}
                      className="px-3 py-1 text-xs text-label bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      Undo Last Point
                    </button>
                    <button
                      onClick={handleClearPolygon}
                      disabled={polygonPoints.length === 0}
                      className="px-3 py-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50"
                    >
                      Clear All Points
                    </button>
                  </div>
                )}
              </div>
              {/* Geofence Type Toggle and Radius (only when editing) */}
              {isEditing && (
                <div className="mb-3">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-label">Circle</span>
                    <Toggle
                      checked={geofenceType === 'Polygon'}
                      onChange={(checked) => {
                        setGeofenceType(checked ? 'Polygon' : 'Circle');
                        if (checked) {
                          // Switching to Polygon - clear polygon points and hide circle
                          setPolygonPoints([]);
                          setCircleCenter(null);
                        } else {
                          // Switching to Circle - use map center as default and clear polygon
                          setCircleCenter(mapCenter);
                          setPolygonPoints([]);
                        }
                      }}
                      variant="simple"
                      size="md"
                      name="geofence-type"
                      ariaLabel="Toggle geofence type"
                    />
                    <span className="text-sm font-medium text-label">Polygon</span>

                    {/* Radius input for Circle - inline */}
                    {geofenceType === 'Circle' && (
                      <div className="flex items-center gap-2 ml-4">
                        <label className="text-sm font-medium text-label">
                          Radius:
                        </label>
                        <input
                          type="number"
                          value={radiusMiles}
                          onChange={(e) => setRadiusMiles(Number(e.target.value))}
                          min="0.01"
                          max="50"
                          step="0.1"
                          className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">miles</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
              {loadError ? (
                <div className="flex items-center justify-center h-96 bg-gray-100 dark:bg-gray-900">
                  <p className="text-red-600 dark:text-red-400">Error loading map</p>
                </div>
              ) : !isLoaded ? (
                <div className="flex items-center justify-center h-96 bg-gray-100 dark:bg-gray-900">
                  <p className="text-gray-600 dark:text-gray-400">Loading map...</p>
                </div>
              ) : (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={mapZoom}
                  onLoad={onMapLoad}
                  onClick={handleMapClick}
                  options={{
                    ...getMapOptions(isDarkMode),
                    disableDoubleClickZoom: true, // Prevent zoom on double-click so polygon can handle it
                    gestureHandling: 'greedy', // Allow all gestures
                  }}
                >
                  {renderGeofence()}
                </GoogleMap>
              )}
            </div>

            {/* Coordinates Info Box - fills remaining space */}
            <div className="mt-4 bg-gray-50 dark:bg-gray-900 rounded p-3 border border-gray-200 dark:border-gray-700 flex flex-col flex-1 overflow-hidden">
              <h4 className="text-sm font-semibold text-label mb-2">Geofence Data</h4>
              {geofenceType === 'Circle' && circleCenter && (
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Center:</span>
                    <span className="text-heading">
                      {circleCenter.lat.toFixed(6)}, {circleCenter.lng.toFixed(6)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Radius:</span>
                    <span className="text-heading">{radiusMiles.toFixed(2)} miles</span>
                  </div>
                </div>
              )}
              {geofenceType === 'Polygon' && polygonPoints.length > 0 && (
                <div className="space-y-1 text-xs flex flex-col flex-1 overflow-hidden">
                  <div className="text-gray-600 dark:text-gray-400 mb-1">
                    Points ({polygonPoints.length}):
                  </div>
                  <div className="overflow-y-auto space-y-1 relative flex-1">
                    {polygonPoints.map((point, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedPointIndex(index)}
                        className={`flex items-center gap-2 p-1 rounded cursor-pointer transition-colors ${
                          selectedPointIndex === index
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                            : 'text-heading hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        {isEditing && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemovePoint(index);
                            }}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-lg font-bold leading-none"
                            title="Remove this point"
                          >
                            ×
                          </button>
                        )}
                        <span className={selectedPointIndex === index ? 'text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400'}>
                          #{index + 1}:
                        </span>
                        <span className="flex-1">{point.lat.toFixed(6)}, {point.lng.toFixed(6)}</span>
                      </div>
                    ))}

                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Drawer>

      {/* Notification Toast */}
      {notification && (
        <NotificationContainer position="top-right">
          <Notification
            type={notification.type}
            title={notification.title}
            message={notification.message}
            showClose={true}
            onClose={() => setNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>
      )}

    </>
  );
};

export default GeofenceDrawer;
